import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { authAPI } from '../services/auth-api.service';

// Types matching your Angular auth functionality
export interface User {
  id: string;
  name: string;
  email: string;
  roles: {
    role_name: string;
    display_name: string;
    permissions: string[];
  };
  profile: {
    emp_id: string;
    mobile: string;
    dept_code: string;
    division: string;
    created_by: string;
  };
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Multi-step auth state (matching Angular)
  authStep: 'login' | 'mfa' | 'forgot-password' | 'reset-password' | 'sso' | 'change-password' | 'signup';
  mfaMethod: 'sms' | 'email' | 'app' | null;
  otpSent: boolean;
  otpReferenceId: string | null;
  email: string | null; // Store email for MFA/resend OTP
  tempCredentials: { email: string; password: string } | null; // Temp storage for resend OTP
  
  // Form state (matching Angular)
  loginAttempts: number;
  maxAttempts: number;
  isAccountLocked: boolean;
  passwordExpiry: boolean;
  
  // SSO state (matching Angular)
  availableSSOProviders: string[];
  canShowSSOLogin: boolean;
  
  // Password policy (from Angular sessionStorage)
  passwordPolicy: {
    minLength: number;
    maxLength: number;
  };
}

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  
  authStep: 'login',
  mfaMethod: null,
  otpSent: false,
  otpReferenceId: null,
  email: null,
  tempCredentials: null,
  
  loginAttempts: 0,
  maxAttempts: 5,
  isAccountLocked: false,
  passwordExpiry: false,
  
  availableSSOProviders: [],
  canShowSSOLogin: false,
  
  passwordPolicy: {
    minLength: 8,
    maxLength: 30,
  },
};

// Async thunks using authAPI service (matches Angular SiteService API calls)
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      return response.data;
    } catch (error: any) {
      // Extract error message from httpClient's ApiError
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const sendForgotPasswordOTP = createAsyncThunk(
  'auth/sendForgotPasswordOTP',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await authAPI.sendForgotPasswordOTP(email);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to send OTP');
    }
  }
);

export const verifyOTP = createAsyncThunk(
  'auth/verifyOTP',
  async (otpData: { otp: string; reference_id: string }, { rejectWithValue }) => {
    try {
      const response = await authAPI.verifyForgotPasswordOTP(otpData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'OTP verification failed');
    }
  }
);

export const resendLoginOTP = createAsyncThunk(
  'auth/resendLoginOTP',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      // Same API as login() to resend OTP - matches Angular logic
      const response = await authAPI.login(credentials);
      return {
        reference_id: response.data.data.reference_id,
        message: response.data.message || 'OTP sent successfully'
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to resend OTP');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (resetData: { email: string; newpassword: string; oldpassword: string }, { rejectWithValue }) => {
    try {
      const response = await authAPI.resetPassword(resetData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Password reset failed');
    }
  }
);

export const performSSOLogin = createAsyncThunk(
  'auth/performSSOLogin',
  async (ssoData: { userId: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authAPI.loginSSO(ssoData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'SSO login failed');
    }
  }
);

export const changePasswordFirstLogin = createAsyncThunk(
  'auth/changePasswordFirstLogin',
  async (passwordData: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authAPI.changePasswordFirstLogin(
        passwordData.email,
        passwordData.password
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Password change failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Synchronous actions
    clearError: (state) => {
      state.error = null;
    },
    
    setAuthStep: (state, action: PayloadAction<AuthState['authStep']>) => {
      state.authStep = action.payload;
      state.error = null; // Clear error when changing steps
    },
    
    setMfaMethod: (state, action: PayloadAction<AuthState['mfaMethod']>) => {
      state.mfaMethod = action.payload;
    },
    
    incrementLoginAttempts: (state) => {
      state.loginAttempts += 1;
      if (state.loginAttempts >= state.maxAttempts) {
        state.isAccountLocked = true;
      }
    },
    
    resetLoginAttempts: (state) => {
      state.loginAttempts = 0;
      state.isAccountLocked = false;
    },
    
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.authStep = 'login';
      state.error = null;
      state.loginAttempts = 0;
      state.isAccountLocked = false;
      state.otpSent = false;
      state.otpReferenceId = null;
      
      // Clear session storage (Angular compatibility)
      sessionStorage.removeItem('finpro_k_Authorization');
      sessionStorage.removeItem('loggedUserInfo');
    },
    
    // Initialize from session storage (for Angular compatibility)
    initializeFromStorage: (state) => {
      const token = sessionStorage.getItem('finpro_k_Authorization');
      const userInfo = sessionStorage.getItem('loggedUserInfo');
      
      if (token && userInfo) {
        try {
          const user = JSON.parse(atob(userInfo));
          state.user = user;
          state.token = token;
          state.isAuthenticated = true;
        } catch (error) {
          console.error('Failed to parse user info from session storage');
        }
      }
      
      // Initialize password policy from session storage (matching Angular)
      const pwdMin = sessionStorage.getItem('pwdmin');
      const pwdMax = sessionStorage.getItem('pwdmax');

      if (pwdMin) state.passwordPolicy.minLength = Number(pwdMin);
      if (pwdMax) state.passwordPolicy.maxLength = Number(pwdMax);

      // Restore authStep to 'change-password' if user was in password change flow
      // This handles the case when AuthGuard redirects back to /login
      const changePasswordReason = sessionStorage.getItem('change_password_reason');
      if (changePasswordReason && token) {
        state.authStep = 'change-password';
      }
    },
    
    // SSO configuration (matching Angular environment)
    setSSOConfig: (state, action: PayloadAction<{ providers: string[]; canShow: boolean }>) => {
      state.availableSSOProviders = action.payload.providers;
      state.canShowSSOLogin = action.payload.canShow;
    },

    // Store credentials for MFA resend OTP (called by LoginForm)
    storeCredentialsForMFA: (state, action: PayloadAction<{ email: string; password: string; referenceId: string }>) => {
      state.email = action.payload.email;
      state.tempCredentials = {
        email: action.payload.email,
        password: action.payload.password,
      };
      state.otpReferenceId = action.payload.referenceId;
      state.authStep = 'mfa';
      state.otpSent = true;
    },
  },
  
  extraReducers: (builder) => {
    // Login user cases
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;

        // Access nested data property from LoginResponse
        if (action.payload.data.multiAuthentication) {
          // Multi-factor authentication required
          state.authStep = 'mfa';
          state.mfaMethod = 'sms'; // Default to SMS like Angular
          state.otpSent = true;
          state.otpReferenceId = action.payload.data.reference_id || null;
        } else {
          // Direct login success - store token
          state.token = action.payload.data.token;
          state.isAuthenticated = true;

          // Store token in session storage for Angular compatibility
          sessionStorage.setItem('finpro_k_Authorization', state.token);
        }

        state.loginAttempts = 0;
        state.isAccountLocked = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.loginAttempts += 1;
        
        if (state.loginAttempts >= state.maxAttempts) {
          state.isAccountLocked = true;
          state.error = 'Account locked due to too many failed attempts';
        }
      });
      
    // Send forgot password OTP cases
    builder
      .addCase(sendForgotPasswordOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendForgotPasswordOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        state.otpSent = true;
        state.otpReferenceId = action.payload.data.reference_id;
        state.authStep = 'forgot-password'; // Stay on forgot password step for OTP entry
      })
      .addCase(sendForgotPasswordOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
      
    // Verify OTP cases
    builder
      .addCase(verifyOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.isLoading = false;

        if (state.authStep === 'mfa') {
          // MFA verification for login - store token
          state.token = action.payload.data.token;
          state.isAuthenticated = true;
          state.authStep = 'login';

          // Store token in session storage
          sessionStorage.setItem('finpro_k_Authorization', state.token);
        } else if (state.authStep === 'forgot-password') {
          // OTP verification for password reset
          state.authStep = 'reset-password';
        }
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Resend Login OTP cases
    builder
      .addCase(resendLoginOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resendLoginOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        state.otpReferenceId = action.payload.reference_id || null;
        // Success message will be shown by component
      })
      .addCase(resendLoginOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Reset password cases
    builder
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.authStep = 'login';
        state.otpSent = false;
        state.otpReferenceId = null;
        // Success message will be shown by the component
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
      
    // SSO login cases
    builder
      .addCase(performSSOLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(performSSOLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.data.token;
        state.isAuthenticated = true;

        // Store token in session storage
        sessionStorage.setItem('finpro_k_Authorization', state.token);
      })
      .addCase(performSSOLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Change password (first-time login) cases
    builder
      .addCase(changePasswordFirstLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePasswordFirstLogin.fulfilled, (state) => {
        state.isLoading = false;
        state.authStep = 'login';
        // Clear session - user must login with new password
        sessionStorage.removeItem('finpro_k_Authorization');
        sessionStorage.removeItem('loggedUserInfo');
      })
      .addCase(changePasswordFirstLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setAuthStep,
  setMfaMethod,
  incrementLoginAttempts,
  resetLoginAttempts,
  logout,
  initializeFromStorage,
  setSSOConfig,
  storeCredentialsForMFA,
} = authSlice.actions;

export default authSlice.reducer;